import { Button, Checkbox, Form, Input, Modal, Popconfirm, Radio, Select, Tag, Upload, UploadProps } from 'antd'
import React from 'react'
import moment from 'moment'
import { DeleteFilled, InboxOutlined, UndoOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import axios, { AxiosProgressEvent } from 'axios'
import noProductImage from 'assets/images/no-image-product.png'
import { bannersApi } from 'api/banners'
import { EndDateCell, EndDateField, FixedPositionFields, reorderRows, SortableTable } from 'components/ui'
import { EBannerStatus, EBannerType, IBanner } from 'models/banners'
import { LOCKED_ROW_HINT } from 'shared/constants/rotation'
import { IMAGES_URL } from 'shared/constants/urls'
import { fromEndDateField, toEndDateField } from 'shared/utils/endDate'
import { formatAppScreen, getAppScreenFromUrl } from 'shared/utils/bannerAppScreen'
import { normalizeFile } from 'shared/utils/normalizeFile'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { BannerMediaPreview } from './components/BannerMediaPreview'
import { BannerSavingModal } from './components/BannerSavingModal'
import { HeaderList } from './components/HeaderList'
import { VideoSizes } from './components/VideoSizes'
import styles from './BannerList.module.scss'

const PHOTO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
const VIDEO_MIME_TYPES = ['video/mp4', 'video/webm']
const MAX_POLO_VIDEO_BYTES = 300 * 1024 * 1024

const videoUploadProps: UploadProps = {
  maxCount: 1,
  accept: 'video/mp4,video/webm',
  beforeUpload: (file) => {
    if (!VIDEO_MIME_TYPES.includes(file.type)) {
      showMessage('error', 'Поддерживаются только форматы MP4 и WebM', '')
      return Upload.LIST_IGNORE
    }
    if (file.size > MAX_POLO_VIDEO_BYTES) {
      showMessage('error', 'Видео должно быть не больше 300 МБ', '')
      return Upload.LIST_IGNORE
    }
    return true
  },
  customRequest: ({ onSuccess }) => onSuccess?.('ok')
}

const photoUploadProps: UploadProps = {
  maxCount: 1,
  accept: 'image/png,image/jpeg,image/jpg,image/webp',
  beforeUpload: (file) => {
    if (!PHOTO_MIME_TYPES.includes(file.type)) {
      showMessage('error', 'Поддерживаются только форматы JPG, JPEG, PNG, WebP', '')
      return Upload.LIST_IGNORE
    }
    return true
  },
  customRequest: ({ onSuccess }) => onSuccess?.('ok')
}

// баннер — либо фото, либо видео: пока выбран файл другого вида, кнопка не открывает
// выбор файла, а перетащенный файл не принимается
const lockUpload = (props: UploadProps, lockMessage: string | null): UploadProps => {
  if (!lockMessage) return props
  return {
    ...props,
    openFileDialogOnClick: false,
    beforeUpload: () => {
      showMessage('error', lockMessage, '')
      return Upload.LIST_IGNORE
    }
  }
}

const getVideoFileList = (video?: string | null) =>
  video ? [{ uid: '-1', name: 'Текущее видео', status: 'done', url: `${IMAGES_URL}${video}` }] : []

// app-баннер ведёт либо на экран приложения, либо на страницу в браузере телефона
enum EAppTarget {
  SCREEN = 'SCREEN',
  URL = 'URL'
}

const getFormValues = (banner: IBanner | null) => ({
  end_date: toEndDateField(banner?.end_date),
  fixed_position: banner?.fixed_position ?? false,
  type: banner?.type ?? EBannerType.WEB_APP,
  video_loop: banner?.video_loop ?? false,
  webUrl: banner?.webUrl ?? undefined,
  scrollDown: banner?.scrollDown ?? false,
  navigation_path: banner?.navigation_path ?? undefined,
  appTarget:
    banner?.type === EBannerType.APP && banner.webUrl && !banner.navigation_path
      ? EAppTarget.URL
      : EAppTarget.SCREEN,
  // текущее фото/видео как уже загруженный элемент — не требует повторной загрузки;
  // у видеобаннера фото — первый кадр ролика, отдельным файлом его не показываем
  photo: banner?.photo && !banner.video
    ? [
      {
        uid: '-1',
        name: 'Текущее фото',
        status: 'done',
        url: `${IMAGES_URL}${banner.photo}`
      }
    ]
    : [],
  video: getVideoFileList(banner?.video)
})

const getLinkFields = (values: any) => {
  if (values.type === EBannerType.APP) {
    // пустая строка стирает на бэкенде поле другого режима, иначе оно переживёт смену режима
    return values.appTarget === EAppTarget.URL
      ? { webUrl: values.webUrl, navigation_path: '' }
      : { webUrl: '', navigation_path: values.navigation_path ?? '' }
  }
  // у web+app экран выводит бэкенд из ссылки, своё значение шлёт только APP
  return {
    scrollDown: !!values.scrollDown,
    // scrollDown заменяет webUrl: при включённом чекбоксе ссылку не шлём
    webUrl: values.scrollDown ? undefined : values.webUrl
  }
}

// Ссылка, вставленная в поле экрана, но не узнанная как экран, даст некликабельный баннер
const checkScreenLink = (_: unknown, value?: string) =>
  value?.includes('/') && !getAppScreenFromUrl(value)
    ? Promise.reject(
      new Error('У этой ссылки нет экрана в приложении — выберите «Ссылка в браузере»')
    )
    : Promise.resolve()

interface BannerListProps {
  // при передаче — список/создание скоупятся на лендинг-страницу (напр. ПолоКлуб)
  pageSlug?: string
  pageId?: string
}

export const BannerList: React.FC<BannerListProps> = ({ pageSlug, pageId }) => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [banners, setBanners] = React.useState<IBanner[]>([])
  const [showArchived, setShowArchived] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  // сколько процентов видео отправлено; null — видео не сохраняется
  const [videoUploadProgress, setVideoUploadProgress] = React.useState<number | null>(null)
  const saveController = React.useRef<AbortController | null>(null)
  // null — режим создания, иначе редактируем этот баннер
  const [editingBanner, setEditingBanner] = React.useState<IBanner | null>(null)
  const [form] = Form.useForm()
  const bannerType = Form.useWatch('type', form)
  const appTarget = Form.useWatch('appTarget', form)
  // web: «пролистать вниз» ставится вместо web-ссылки — одно исключает другое
  const scrollDown = Form.useWatch('scrollDown', form)
  // экран приложения у web+app показываем по ссылке прямо во время ввода
  const webUrlValue = Form.useWatch('webUrl', form)
  // баннер — либо фото, либо видео: при видео фото не обязательно
  const videoList = Form.useWatch('video', form)
  const photoList = Form.useWatch('photo', form)
  const hasVideo = (videoList?.length ?? 0) > 0
  const hasPhoto = (photoList?.length ?? 0) > 0
  const photoLock = hasVideo ? 'Чтобы загрузить фото, удалите видео' : null
  const videoLock = hasPhoto ? 'Чтобы загрузить видео, удалите фото' : null
  const webUrlScreen = getAppScreenFromUrl(scrollDown ? null : webUrlValue)

  React.useEffect(() => {
    if (hasVideo) form.setFields([{ name: 'photo', errors: [] }])
  }, [form, hasVideo])

  const getBanners = async () => {
    try {
      setIsLoading(true)
      const response = await bannersApi.getBanners(pageSlug)
      setBanners(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getBanners()
  }, [pageSlug])

  // список приходит в порядке показа; закреплённые не перетаскиваются
  const activeBanners = banners.filter((banner) => banner.status !== EBannerStatus.ARCHIVED)
  const sortableIds = activeBanners.filter((banner) => !banner.fixed_position).map((banner) => banner._id)
  const lockedIds = activeBanners.filter((banner) => banner.fixed_position).map((banner) => banner._id)

  const handleSortEnd = async (ids: string[]) => {
    setBanners((prev) => reorderRows(prev, ids))
    try {
      await bannersApi.reorderBanners(ids, pageSlug)
    } catch (e) {
      showServerError(e)
      await getBanners()
    }
  }

  // инстанс формы переживает закрытие модалки и при следующем открытии
  // перекрывает initialValues сохранёнными значениями, поэтому заполняем руками
  React.useEffect(() => {
    if (isModalOpen) form.setFieldsValue(getFormValues(editingBanner))
  }, [isModalOpen, editingBanner])

  const handleArchive = async (id: string) => {
    try {
      await bannersApi.updateBannerStatus(id, EBannerStatus.ARCHIVED)
      await getBanners()
    } catch (e) {
      showServerError(e)
    }
  }

  const handleRestore = async (id: string) => {
    try {
      await bannersApi.updateBannerStatus(id, EBannerStatus.PUBLISHED)
      await getBanners()
    } catch (e) {
      showServerError(e)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await bannersApi.deleteBanner(id)
      await getBanners()
    } catch (e) {
      showServerError(e)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingBanner(null)
    form.resetFields()
  }

  const openCreate = () => {
    setEditingBanner(null)
    setIsModalOpen(true)
  }

  const openEdit = (banner: IBanner) => {
    setEditingBanner(banner)
    setIsModalOpen(true)
  }

  const handleSubmit = async (values: any) => {
    const photo = values.photo?.[0]?.originFileObj
    const video = values.video?.[0]?.originFileObj
    const controller = new AbortController()
    saveController.current = controller
    const config = {
      signal: controller.signal,
      onUploadProgress: video
        ? (event: AxiosProgressEvent) => setVideoUploadProgress(Math.round((event.progress ?? 0) * 100))
        : undefined
    }
    try {
      setIsSubmitting(true)
      if (video) setVideoUploadProgress(0)
      const isPolo = pageSlug === 'ruspoloclub'
      // лишние ссылочные поля бэкенд обнулит по итоговому типу
      const payload = {
        // место задают порядок в списке и очередь, у лендинга перемешивания нет
        ...(!pageSlug && { fixed_position: !!values.fixed_position }),
        end_date: fromEndDateField(values.end_date),
        type: values.type,
        ...(isPolo && { video_loop: !!values.video_loop && hasVideo }),
        ...getLinkFields(values),
        // привязка к лендинг-странице только при создании (обновление page_id не меняет)
        ...(pageId && !editingBanner && { page_id: pageId })
      }

      if (editingBanner) {
        await bannersApi.updateBanner(
          editingBanner._id,
          {
            ...payload,
            // файл передаём только если заменили — иначе остаётся прежний
            ...(photo && { photo }),
            ...(video && { video })
          },
          config
        )
      } else {
        await bannersApi.createBanner({ ...payload, photo, video }, config)
      }

      closeModal()
      await getBanners()
    } catch (e) {
      if (axios.isCancel(e)) {
        showMessage('info', 'Сохранение отменено', editingBanner ? 'Баннер остался прежним' : 'Баннер не создан')
        await getBanners()
      } else {
        showServerError(e)
      }
    } finally {
      setIsSubmitting(false)
      setVideoUploadProgress(null)
      saveController.current = null
    }
  }

  const displayedBanners = showArchived
    ? banners
    : banners.filter((b) => b.status !== EBannerStatus.ARCHIVED)

  const columns: ColumnsType<IBanner> = [
    {
      title: 'Фото',
      dataIndex: 'photo',
      width: 100,
      render: (_, { photo, video }) =>
        video ? (
          <video
            src={`${IMAGES_URL}${video}`}
            width={80}
            height={56}
            muted
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : photo ? (
          <div
            style={{
              width: 80,
              height: 56,
              backgroundImage: `url(${IMAGES_URL}${photo})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderRadius: 4
            }}
          />
        ) : (
          <img src={noProductImage} alt='Баннер' width={80} height={56} />
        )
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (_, { type }) => {
        const color =
          type === EBannerType.WEB
            ? 'blue'
            : type === EBannerType.APP
              ? 'green'
              : 'purple'
        return <Tag color={color}>{type === EBannerType.WEB_APP ? 'WEB+APP' : type}</Tag>
      }
    },
    {
      title: 'Ссылка / Экран',
      key: 'link',
      // длинный адрес переносится, и колонка уступает ширину остальным
      onCell: () => ({ style: { overflowWrap: 'anywhere' } }),
      render: (_, { type, webUrl, scrollDown, navigation_path, navigation_params }) => {
        const screen = formatAppScreen(navigation_path, navigation_params)
        // scrollDown заменяет web-ссылку — показываем это вместо URL
        const web = scrollDown ? 'Пролистать вниз ↓' : webUrl || '—'
        // web+app показывает и ссылку, и экран — двумя строками с подписями
        if (type === EBannerType.WEB_APP) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>
                <strong>Ссылка</strong>: {web}
              </span>
              <span>
                <strong>Экран</strong>: {screen || '—'}
              </span>
            </div>
          )
        }
        if (scrollDown) return 'Пролистать вниз ↓'
        if (webUrl) return webUrl
        if (screen) return screen
        return '—'
      }
    },
    {
      title: 'Дата создания',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (_, { createdAt }) => (createdAt ? moment(createdAt).format('DD.MM.YYYY') : '—')
    },
    {
      title: 'Дата окончания',
      dataIndex: 'end_date',
      key: 'end_date',
      width: 150,
      render: (_, { end_date }) => <EndDateCell value={end_date} />
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (_, { status }) => (
        <Tag color={status === EBannerStatus.PUBLISHED ? 'success' : 'default'}>
          {status === EBannerStatus.PUBLISHED ? 'Активен' : 'Архив'}
        </Tag>
      )
    },
    {
      title: '',
      dataIndex: 'operation',
      width: 100,
      // клик по кнопкам и их подтверждениям не должен открывать баннер, как клик по строке
      render: (_, banner) => {
        if (banner.status === EBannerStatus.ARCHIVED) {
          return (
            <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
              <Button
                icon={<UndoOutlined />}
                size='small'
                onClick={() => handleRestore(banner._id)}
              />
              <Popconfirm
                title='Удалить баннер безвозвратно?'
                onConfirm={() => handleDelete(banner._id)}
              >
                <Button type='primary' danger icon={<DeleteFilled />} size='small' />
              </Popconfirm>
            </div>
          )
        }
        return (
          <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
            <Popconfirm title='Отправить в архив?' onConfirm={() => handleArchive(banner._id)}>
              <Button type='primary' danger icon={<InboxOutlined />} size='small' />
            </Popconfirm>
          </div>
        )
      }
    }
  ]

  return (
    <>
      <div style={{ paddingBottom: 80 }}>
        <HeaderList
          onClick={openCreate}
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
        />
        <SortableTable
          scroll={{ x: 750 }}
          pagination={false}
          columns={columns}
          dataSource={displayedBanners}
          loading={isLoading}
          rowClassName='tableRow'
          onRow={(banner) => ({ onClick: () => openEdit(banner) })}
          sortableIds={sortableIds}
          lockedIds={lockedIds}
          lockedHint={LOCKED_ROW_HINT}
          onSortEnd={handleSortEnd}
        />
      </div>

      <Modal
        title={editingBanner ? 'Редактировать баннер' : 'Добавить баннер'}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          className={styles.form}
          layout='vertical'
          onFinish={handleSubmit}
          initialValues={getFormValues(null)}
        >
          <BannerMediaPreview photo={photoList?.[0]} video={videoList?.[0]} />

          <Form.Item
            label='Фото'
            name='photo'
            valuePropName='fileList'
            getValueFromEvent={normalizeFile}
            rules={[
              pageSlug === 'ruspoloclub'
                ? { required: !hasVideo, message: 'Добавьте фото или видео' }
                : { required: true, message: 'Добавьте фото' }
            ]}
            extra='Соотношение сторон 2:1. JPG, PNG или WebP.'
          >
            <Upload {...lockUpload(photoUploadProps, photoLock)}>
              <Button onClick={() => photoLock && showMessage('error', photoLock, '')}>Загрузить фото</Button>
            </Upload>
          </Form.Item>

          {pageSlug === 'ruspoloclub' && (
            <>
              <Form.Item
                label='Видео вместо фото'
                name='video'
                valuePropName='fileList'
                getValueFromEvent={normalizeFile}
                extra={
                  <>
                    <div>MP4 или WebM до 300 МБ и 2 минут.</div>
                    <div>Из ролика делаются две версии:</div>
                    <div>• ПК-версия сайта (экраны от 1024 px) — 1920×1080, до 5 Мбит/с;</div>
                    <div>• мобильный веб и приложение — 1280×720, до 2,5 Мбит/с.</div>
                    <VideoSizes
                      file={videoList?.[0]}
                      video={editingBanner?.video}
                      videoDesktop={editingBanner?.video_desktop}
                    />
                  </>
                }
              >
                <Upload {...lockUpload(videoUploadProps, videoLock)}>
                  <Button onClick={() => videoLock && showMessage('error', videoLock, '')}>Загрузить видео</Button>
                </Upload>
              </Form.Item>
              <Form.Item name='video_loop' valuePropName='checked'>
                <Checkbox disabled={!hasVideo}>Повторять видео по кругу</Checkbox>
              </Form.Item>
            </>
          )}

          {!pageSlug && <FixedPositionFields />}

          <EndDateField subject='баннер' />

          <Form.Item label='Тип' name='type' rules={[{ required: true }]}>
            <Select>
              <Select.Option value={EBannerType.WEB_APP}>WEB + APP</Select.Option>
              <Select.Option value={EBannerType.APP}>APP</Select.Option>
              <Select.Option value={EBannerType.WEB}>WEB</Select.Option>
            </Select>
          </Form.Item>

          {(bannerType === EBannerType.WEB ||
            bannerType === EBannerType.WEB_APP) && (
            <>
              <Form.Item name='scrollDown' valuePropName='checked'>
                <Checkbox>
                  Пролистать страницу вниз (вместо перехода по ссылке)
                </Checkbox>
              </Form.Item>

              {!scrollDown && (
                <Form.Item
                  label='URL (подборка / продавец / товар)'
                  name='webUrl'
                  // на лендинге баннер бывает просто картинкой, без перехода
                  rules={[{ required: !pageSlug, message: 'Укажите URL' }]}
                >
                  <Input placeholder='https://horsesmart.store/...' />
                </Form.Item>
              )}
            </>
          )}

          {bannerType === EBannerType.WEB_APP && (
            <Form.Item
              label='Экран в приложении'
              extra='Определяется по ссылке баннера: в приложении баннер ведёт туда же, куда на сайте.'
            >
              <Input
                disabled
                value={formatAppScreen(
                  webUrlScreen?.navigation_path,
                  webUrlScreen?.navigation_params
                )}
                placeholder='У этой ссылки нет экрана в приложении — приложение откроет её в браузере'
              />
            </Form.Item>
          )}

          {bannerType === EBannerType.APP && (
            <Form.Item
              label='Куда ведёт баннер'
              name='appTarget'
              tooltip='Экран приложения — товар, продавец, подборка или раздел открываются внутри приложения. Ссылка — любая страница, например лендинг РусПолоКлуба, открывается в браузере телефона. Старые версии приложения по ссылке не переходят.'
            >
              <Radio.Group>
                <Radio value={EAppTarget.SCREEN}>Экран приложения</Radio>
                <Radio value={EAppTarget.URL}>Ссылка в браузере</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {bannerType === EBannerType.APP && appTarget !== EAppTarget.URL && (
            <Form.Item
              label='Экран в приложении'
              name='navigation_path'
              extra='Вставьте ссылку на товар, продавца или подборку — экран определится по ней.'
              rules={[{ validator: checkScreenLink, warningOnly: true }]}
            >
              <Input placeholder='https://horsesmart.store/product/...' />
            </Form.Item>
          )}

          {bannerType === EBannerType.APP && appTarget === EAppTarget.URL && (
            <Form.Item
              label='Ссылка'
              name='webUrl'
              rules={[
                { required: true, message: 'Укажите ссылку' },
                { pattern: /^https?:\/\/\S+$/i, message: 'Ссылка должна начинаться с https://' }
              ]}
              extra={
                webUrlScreen
                  ? 'Для этой страницы есть экран в приложении — выберите «Экран приложения», чтобы открыть её без браузера.'
                  : undefined
              }
            >
              <Input placeholder='https://horsesmart.store/ruspoloclub' />
            </Form.Item>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button onClick={closeModal}>Отмена</Button>
            <Button type='primary' htmlType='submit' loading={isSubmitting}>
              {editingBanner ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </Form>
      </Modal>

      <BannerSavingModal progress={videoUploadProgress} onCancel={() => saveController.current?.abort()} />
    </>
  )
}
